import type { AccountModel } from "@/api";
import snapshot from "./preprodDataSourceSnapshot.json";

const SOURCE_TYPES = new Set([
  "snowflake",
  "microsoft365",
  "dremio",
  "databricks",
  "mysql",
  "powerbi",
  "oracle",
  "sqlserver",
  "aws",
  "aurora",
  "s3",
  "dynamodb",
  "emr",
]);
const STATUSES = new Set([
  "ACTIVE",
  "MISSING CONNECTION",
  "INACTIVE",
  "FAILED LOGIN",
]);
const COUNTS = [
  "monitoringPolicyCount",
  "dataAccessPolicyCount",
  "alertCount",
  "notificationErrorsCount",
] as const;

/** Allowlist at the boundary: credentials in a replacement export never escape. */
export function adaptDataSourceSnapshot(input: unknown): AccountModel[] {
  if (!Array.isArray(input))
    throw new Error(
      "Data Source snapshot must be an array of account summaries.",
    );
  const ids = new Set<string>();
  return input.map((value: unknown, index) => {
    if (!value || typeof value !== "object")
      throw new Error(
        `Data Source snapshot row ${index + 1} must be an object.`,
      );
    const row = value as Record<string, unknown>;
    const required = ["id", "name", "type", "accountStatus"] as const;
    for (const key of required) {
      if (typeof row[key] !== "string" || !row[key].trim())
        throw new Error(
          `Data Source snapshot row ${index + 1}: missing ${key}.`,
        );
    }
    const id = row.id as string;
    if (ids.has(id))
      throw new Error(`Data Source snapshot row ${index + 1}: duplicate id.`);
    ids.add(id);
    if (!SOURCE_TYPES.has(row.type as string))
      throw new Error(
        `Data Source snapshot row ${index + 1}: unsupported type.`,
      );
    if (!STATUSES.has(row.accountStatus as string))
      throw new Error(
        `Data Source snapshot row ${index + 1}: unsupported accountStatus.`,
      );
    const account: AccountModel = {
      id,
      name: row.name as string,
      type: row.type as string,
      accountStatus: row.accountStatus as string,
    };
    for (const key of COUNTS) {
      const count = row[key];
      if (count === undefined || count === null) continue;
      if (typeof count !== "number" || !Number.isInteger(count) || count < 0)
        throw new Error(
          `Data Source snapshot row ${index + 1}: ${key} must be a nonnegative integer.`,
        );
      account[key] = count;
    }
    if (typeof row.dataClassificationScheme === "string")
      account.dataClassificationScheme = row.dataClassificationScheme;
    if (typeof row.lastModifiedDate === "string") {
      if (Number.isNaN(Date.parse(row.lastModifiedDate)))
        throw new Error(
          `Data Source snapshot row ${index + 1}: invalid lastModifiedDate.`,
        );
      account.lastModifiedDate = new Date(row.lastModifiedDate);
    }
    return account;
  });
}

export const loadDataSourceSnapshot = (
  input: unknown,
  fallback: unknown = snapshot,
) => ({
  items: adaptDataSourceSnapshot(input ?? fallback),
  provenance:
    input == null ? ("FALLBACK" as const) : ("PREPROD_SNAPSHOT" as const),
});

/**
 * Module scope, and `mockRegistry` imports this file - so a throw here would
 * white-screen the app for every user, demo or not. Degrade to an empty list
 * and let the page's own empty state carry it instead.
 */
export const dataSourceSnapshot = (() => {
  try {
    return loadDataSourceSnapshot(snapshot);
  } catch {
    return { items: [] as AccountModel[], provenance: "FALLBACK" as const };
  }
})();
export const snapshotAccounts = dataSourceSnapshot.items;
